import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { setRegistrationData } from '../store/slices/registrationSlice';
import Layout from '../components/Layout';
import authService from '../services/auth';
import './Auth.css';

const Signup = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10,11}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone number is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setLoading(true);
    setMessage('');
    setErrors({});

    try {
      // Format phone number (remove spaces, ensure it starts with + or country code)
      let phone = formData.phone.replace(/\s+/g, '');
      if (!phone.startsWith('+') && !phone.startsWith('234')) {
        // If it starts with 0, replace with +234
        if (phone.startsWith('0')) {
          phone = '+234' + phone.substring(1);
        } else {
          phone = '+234' + phone;
        }
      } else if (phone.startsWith('234')) {
        phone = '+' + phone;
      }

      // Send registration OTP (does not create account)
      const response = await authService.sendRegistrationOtp({
        email: formData.email,
        password: formData.password,
        phone: phone
      });

      // Store registration data in Redux
      dispatch(setRegistrationData({
        email: formData.email,
        phone: phone,
        password: formData.password
      }));

      // Show success message
      setMessage('Verification codes have been sent to your email and phone.');

      // Navigate to email verification page after a short delay
      setTimeout(() => {
        navigate('/verify-email');
      }, 1500);

    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle validation errors from backend
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        // Handle Laravel validation errors
        if (errorData.errors) {
          const newErrors = {};
          Object.keys(errorData.errors).forEach(key => {
            newErrors[key] = errorData.errors[key][0];
          });
          setErrors(newErrors);
        } else if (errorData.message) {
          setMessage(errorData.message);
        }
      } else {
        setMessage(error.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <h1>Create Account</h1>
            <p className="auth-subtitle">Sign up to get started</p>

            {message && (
              <div className={message.includes('successful') ? 'success-message' : 'error-message'}>
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="Enter your email"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={errors.password ? 'error' : ''}
                  placeholder="Create a password"
                />
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={errors.confirmPassword ? 'error' : ''}
                  placeholder="Confirm your password"
                />
                {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                  placeholder="Enter your phone number"
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>

              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? 'Creating Account...' : 'Continue'}
              </button>
            </form>

            <p className="auth-footer">
              Already have an account? <a href="/login">Login</a>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Signup;
